import { NavLink } from "react-router-dom";
import { Home, Building2, Building, Users, Webhook, ScrollText, KeyRound, Settings, ChevronsUpDown, Check } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const items = [
  { to: "/", label: "Overview", icon: Home, end: true },
  { to: "/organisations", label: "Organisations", icon: Building2 },
  { to: "/companies", label: "Companies", icon: Building },
  { to: "/users", label: "Members", icon: Users },
  { to: "/webhooks", label: "Webhooks", icon: Webhook },
  { to: "/api-keys", label: "API Keys", icon: KeyRound },
  { to: "/logs", label: "Logs", icon: ScrollText, badge: "soon" },
  { to: "/settings", label: "Settings", icon: Settings },
];

export function Sidebar() {
  const { user, activeClient, activeClientId, switchClient } = useAuth();
  const clients = user?.clients ?? [];

  return (
    <aside className="hidden h-full w-60 shrink-0 flex-col border-r border-border bg-sidebar/95 px-2.5 py-3.5 backdrop-blur-md md:flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="mb-2 flex w-full items-center gap-2 rounded-md border border-border px-2 py-2 text-left ring-1 ring-ring/50 transition hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            disabled={clients.length === 0}
          >
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Client
              </div>
              <div className="truncate text-sm font-bold text-foreground">
                {activeClient?.clientName ?? "No client"}
              </div>
            </div>
            <ChevronsUpDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Switch client
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {clients.map((c) => {
            const active = c.clientId === activeClientId;
            return (
              <DropdownMenuItem
                key={c.clientId}
                onClick={() => switchClient(c.clientId)}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{c.clientName}</span>
                  <span className="text-[11px] text-muted-foreground">{c.role}</span>
                </div>
                <span
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
                    // Brand blue, not amber: amber is reserved for the dev-login badge.
                    c.type === "Sandbox"
                      ? "bg-secondary/15 text-secondary-foreground"
                      : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                  )}
                >
                  {c.type}
                </span>
                {active && <Check className="size-3.5 text-foreground" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="mb-2 border-b border-dashed border-border" />

      <nav className="flex flex-col gap-0.5">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition",
                isActive
                  ? "border border-border bg-accent font-semibold text-foreground"
                  : "border border-transparent text-muted-foreground hover:bg-accent hover:text-foreground"
              )
            }
          >
            <it.icon className="size-4" />
            <span>{it.label}</span>
            {it.badge && (
              <span className="ml-auto rounded-full border border-border bg-muted px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
                {it.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="flex-1" />

      <div className="flex items-center gap-2 border-t border-dashed border-border px-2 pt-2 text-[11px] uppercase tracking-wider text-muted-foreground">
        {activeClient?.type === "Sandbox" && (
          <span className="rounded-full border border-secondary/40 bg-secondary/10 px-1.5 py-0.5 text-secondary-foreground">
            sandbox
          </span>
        )}
      </div>
    </aside>
  );
}
